import { Link } from '@tanstack/react-router'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui'
import { testDot } from '@/lib/colour'
import { scoreLine, summarise, type Answered, type PatternRow } from '@/lib/learnTest'

const WORD = { right: 'right', partial: 'partly', wrong: 'to revisit', skipped: 'to revisit' } as const

const Dots = ({ results }: { results: PatternRow['results'] }) => (
  <span className="flex items-center gap-1 shrink-0" data-testid="test-row-dots">
    {results.map((r, i) => (
      <span key={i} className={`w-2 h-2 rounded-full ${testDot[r]}`} data-result={r} title={WORD[r]} />
    ))}
  </span>
)

const Row = ({ p, revisit }: { p: PatternRow; revisit: boolean }) => (
  <li
    className="flex items-center gap-1 pl-3.5 pr-1 min-h-14"
    data-testid="test-row"
    data-revisit={revisit || undefined}
  >
    {revisit ? (
      <Link to="/learn/concept/$id" params={{ id: p.id }} className="flex-1 min-w-0 py-2.5 block">
        <span className="flex items-center justify-between gap-3">
          <span className="text-[16px] font-medium truncate">{p.name}</span>
          <Dots results={p.results} />
        </span>
        {p.why && (
          <span className="block mt-1 text-sm leading-5 text-muted" data-testid="test-row-why">
            {p.why}
          </span>
        )}
      </Link>
    ) : (
      <span className="flex-1 min-w-0 py-2.5 flex items-center justify-between gap-3">
        <span className="text-[16px] font-medium truncate">{p.name}</span>
        <Dots results={p.results} />
      </span>
    )}
    {p.url && (
      <a
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        className="w-11 h-11 shrink-0 flex items-center justify-center text-faint"
        aria-label={`${p.name} on Refactoring.Guru`}
        data-testid="test-row-link"
      >
        <ExternalLink size={18} />
      </a>
    )}
  </li>
)

/**
 * The end of a test: the score in plain words, then each pattern, to revisit first, with a way out to read
 * more on Refactoring.Guru. "To revisit" is the only word for wrong.
 */
export const TestSummary = ({
  answered,
  onAgain,
  onMissed,
  onDone,
  none,
}: {
  answered: Answered[]
  onAgain: () => void
  onMissed: () => void
  onDone: () => void
  none: boolean
}) => {
  const s = summarise(answered)
  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4" data-testid="test-summary">
        <h1 className="text-[28px] leading-9 font-semibold" data-testid="test-score">
          {s.total ? scoreLine(s) : 'Nothing answered yet.'}
        </h1>
        {s.total > 0 && (
          <p className="text-[15px] text-muted mt-1" data-testid="test-split">
            {s.right} right, {s.partly} partly, {s.revisit} to revisit
          </p>
        )}
        {s.toRevisit.length > 0 && (
          <>
            <h2 className="text-xs text-faint mt-5 mb-2">To revisit</h2>
            <ul className="group-list" data-testid="test-revisit">
              {s.toRevisit.map((p) => (
                <Row key={p.id} p={p} revisit />
              ))}
            </ul>
          </>
        )}
        {s.strong.length > 0 && (
          <>
            <h2 className="text-xs text-faint mt-5 mb-2">Strong</h2>
            <ul className="group-list" data-testid="test-strong">
              {s.strong.map((p) => (
                <Row key={p.id} p={p} revisit={false} />
              ))}
            </ul>
          </>
        )}
        <div className="h-4" />
      </div>
      <footer className="flex flex-col gap-2 px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] shrink-0">
        {none && (
          <p className="text-sm text-muted text-center" data-testid="test-lot">
            There are no questions for that.
          </p>
        )}
        <Button variant="primary" big className="w-full min-h-12" onClick={onAgain} data-testid="test-again">
          Again
        </Button>
        {s.missed.length > 0 && (
          <Button big className="w-full min-h-12" onClick={onMissed} data-testid="test-again-missed">
            Again, just what I missed
          </Button>
        )}
        <Button variant="quiet" className="min-h-11" onClick={onDone} data-testid="test-done">
          Done
        </Button>
      </footer>
    </>
  )
}
